import React from "react";
import { array, bool, object, string } from "prop-types";
import { LazyLoadImages, WithLazy, ResponsiveImage } from "@quintype/components";
import { connect } from "react-redux";

import Icon from "../../icons";
import Anchor from "../Anchor";

import { useDevice } from "../../utils/react-utils";
import { calcWidthsWithDpr } from "../../utils/utils";

import "./cardImage.m.css";

const CardImage = ({
  story = {},
  aspectRatio = [1, 1],
  imageType = "image1x1",
  zoom = true,
  widths = [250, 480, 764],
  eager = false,
  showStoryTypeIcon = true,
  showAlternateImage = true,
  defaultImage = true,
  className = "",
  linkEnable = false,
  cfDpr,
}) => {
  const wrapperStyleName = `qt-image-dynamic ${imageType}`;
  const { headline, url } = story;
  const alternativeHeroImage = story.alternative?.home?.default?.["hero-image"];
  const { name } = useDevice();
  const dpr = cfDpr[name] || 1;
  const devices = ["mobile", "tab", "desktop"];

  const widthsWithDpr = calcWidthsWithDpr(widths, dpr);

  const calcDefaultWidth = (() => {
    if (widthsWithDpr.length === 1) return widthsWithDpr[0];

    const mappedWidths = widthsWithDpr.reduce((acc, cur, i) => {
      acc[devices[i]] = cur;

      return acc;
    }, {});

    return mappedWidths[name];
  })();

  const {
    "hero-image-caption": heroImageCaption,
    "hero-image-metadata": heroImageMetaData,
    "hero-image-s3-key": heroImage = showAlternateImage && alternativeHeroImage ? alternativeHeroImage.key : undefined,
  } = showAlternateImage ? alternativeHeroImage || story : story;

  const templates = {
    video: "video-play",
    photo: "photo-camera",
    "visual-story": "visual-story",
    shorts: "shorts",
  };

  const imageLayout = (children) => (
    <div
      styleName={`${wrapperStyleName} card-image-wrapper`}
      style={{ backgroundImage: "url(/media-placeholder.svg)" }}
      className={`${className} ${wrapperStyleName} card-image-wrapper`}
    >
      <figure className="qt-figure">
        <picture className={`qt-image ${zoom ? "zoom-desktop" : ""} ${showAlternateImage ? "default" : ""}`}>
          {children}
        </picture>
      </figure>
      {showStoryTypeIcon && templates[story["story-template"]] && (
        <span styleName="story-type-wrapper" className="story-type-wrapper">
          <Icon type={templates[story["story-template"]]} styleName="story-icon" className="story-icon" />
        </span>
      )}
    </div>
  );

  const imageContainer = (children) => {
    return linkEnable ? (
      <Anchor aria-label={headline} href={url}>
        {imageLayout(children)}
      </Anchor>
    ) : (
      imageLayout(children)
    );
  };

  const loadImage = () => (
    <ResponsiveImage
      slug={heroImage}
      metadata={heroImageMetaData}
      aspectRatio={aspectRatio}
      defaultWidth={calcDefaultWidth || 500}
      widths={widthsWithDpr}
      sizes="(max-width: 500px) 98%, (max-width: 875px) 48%, 23%"
      className="image"
      data-bg="/media-placeholder.svg"
      fetchpriority={eager ? "high" : "low"}
      alt={heroImageCaption || headline}
      imgParams={{ auto: ["format", "compress"], fmt: "avif" }}
    />
  );

  if (!heroImage) {
    return defaultImage
      ? imageContainer(<Icon className={`${className} picture`} type="media-placeholder" styleName="default-image" />)
      : null;
  }

  return imageContainer(
    eager ? loadImage() : <WithLazy margin="50px">{() => <LazyLoadImages>{loadImage()}</LazyLoadImages>}</WithLazy>
  );
};

CardImage.propTypes = {
  story: object,
  imageType: string,
  widths: array,
  zoom: bool,
  eager: bool,
  aspectRatio: array,
  showStoryTypeIcon: bool,
  showAlternateImage: bool,
  defaultImage: bool,
  className: string,
  linkEnable: bool,
  cfDpr: object,
};

const mapStateToProps = (state) => ({
  cfDpr: state.qt.config["publisher-attributes"]["cf-dpr"] || {},
});

export default connect(mapStateToProps)(CardImage);
